import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { z } from 'zod';

import {
    ALL_LINT_CHECKS,
    LINT_CHECK_SEVERITIES,
    type LintCheckName,
    type LintFinding,
    type LintReport,
    runVideoLint,
} from '../lint/videoLinter';
import type { ToolExecutionContext } from '../types';

import { buildCheckFindingCounts } from './checkFindingAnalytics';
import { matchesReferenceFinding } from './reference-video-lint';
import { reviewSkippedResult } from './review-skipping';
import { computeSceneShortIds } from './scene-addressing';
import { computeSceneGroupShortIds } from './scene-group-addressing';
import {
    ALL_AUTO_FIXES,
    AUTO_FIXES,
    type AutoFixName,
    type AutoFixResult,
    isAutoFixName,
} from './lint-auto-fixes';
import { Tool, type ToolConstructorArgs } from './Tool';

import {
    announceCreationStage,
    CreationStage,
    clearAllSceneBuilding,
    isReviewSkipped,
} from 'js/src/data/agent-activity';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { getVideoSizeAnalyticsProperties } from 'js/src/luminary/luminary-utils';

export const lintVideoSchema = z.object({
    check_name: z
        .enum([...ALL_AUTO_FIXES, ...ALL_LINT_CHECKS] as [
            AutoFixName,
            ...(AutoFixName | LintCheckName)[],
        ])
        .optional()
        .describe('Optional single check to run. Omit to run every check.'),
});
export type LintVideoArgs = z.infer<typeof lintVideoSchema>;

/**
 * How the hosting agent's edit tools address a scene, and therefore how the lint report should point
 * at one. The toolbox tools (set_text/set_media/set_icon) take a stable scene_id; the legacy chat
 * tools take a positional scene_index. Findings always carry the stable scene id, and the tool
 * renders whichever locator matches the agent it runs under.
 */
export type SceneAddressing = 'index' | 'scene_id';

export interface LintVideoConstructorArgs extends ToolConstructorArgs {
    sceneAddressing: SceneAddressing;
}

/** Renders the scene portion of a finding's location from its stable scene id. */
type SceneLocatorFormatter = (sceneId: string) => string;

/** Renders the scene-group portion of a finding's location from its scene-group index. */
type GroupLocatorFormatter = (sceneGroupIndex: number) => string;

/**
 * The location prefix shown before a finding's message. Each part is labelled with the same locator
 * the hosting agent's edit tools accept: a scene-level finding via `formatSceneLocator` (a positional
 * index for legacy agents, a short scene_id for the toolbox), and a scene-group-level finding via
 * `formatGroupLocator` (a positional index for legacy agents, a short scene_group_id for the toolbox).
 */
function formatLocation(
    finding: LintFinding,
    formatSceneLocator: SceneLocatorFormatter,
    formatGroupLocator: GroupLocatorFormatter,
): string {
    const parts: string[] = [];
    if (finding.sceneGroupIndex !== undefined) {
        parts.push(formatGroupLocator(finding.sceneGroupIndex));
    }
    if (finding.sceneId !== undefined) {
        const sceneIds = Array.isArray(finding.sceneId) ? finding.sceneId : [finding.sceneId];
        parts.push(...sceneIds.map((sceneId) => formatSceneLocator(sceneId)));
    }
    return parts.length > 0 ? `[${parts.join(', ')}] ` : '';
}

/**
 * Findings listed per severity in a full lint. A long video can produce so many that the report
 * floods the agent's context, so only the first few are shown and the agent re-lints after fixing
 * them. A single-check run is small enough to list in full.
 */
const MAX_FINDINGS_SHOWN_PER_SEVERITY = 5;

/**
 * Warnings are heuristics, so a warning-free lint is not the bar to finish on — but the report used
 * to read as if it were ("confirm each is intentional before finishing"), and the agent answered by
 * fixing every warning and re-verifying after each fix, about half the review stage in production.
 * While errors remain the warnings ride along as a checklist, since the errors force another lint
 * anyway. Once the errors are gone the heading says so outright, so the agent finishes rather than
 * chasing a clean run it was never asked for.
 */
function formatWarningsHeading(hasErrors: boolean): string {
    if (hasErrors) {
        return 'Warnings (weigh each while fixing the errors above; leave the ones that are intentional):';
    }
    return (
        'Warnings (advisory — a warning-free lint is not required to finish. Fix one only if it is ' +
        'clearly wrong for this video, and do not re-lint just to see it clear):'
    );
}

function formatFinding(
    finding: LintFinding,
    formatSceneLocator: SceneLocatorFormatter,
    formatGroupLocator: GroupLocatorFormatter,
): string {
    return `  - ${formatLocation(finding, formatSceneLocator, formatGroupLocator)}${finding.message} (${finding.check})`;
}

export function formatLintReport(
    report: LintReport,
    formatSceneLocator: SceneLocatorFormatter = (sceneId) => `scene_id ${sceneId}`,
    formatGroupLocator: GroupLocatorFormatter = (sceneGroupIndex) =>
        `scene group ${sceneGroupIndex}`,
    maxFindingsShownPerSeverity: number = MAX_FINDINGS_SHOWN_PER_SEVERITY,
): string {
    const errors = report.findings.filter((finding) => finding.severity === 'error');
    const warnings = report.findings.filter((finding) => finding.severity === 'warning');

    if (report.findings.length === 0) {
        return `Video passes all checks (${report.checksRun.length} check(s) run): no errors or warnings.`;
    }

    const lines: string[] = [
        `Lint found ${errors.length} error(s) and ${warnings.length} warning(s).`,
    ];

    const pushSection = (heading: string, findings: LintFinding[], severityLabel: string) => {
        if (findings.length === 0) {
            return;
        }
        lines.push('', heading);
        findings
            .slice(0, maxFindingsShownPerSeverity)
            .forEach((finding) =>
                lines.push(formatFinding(finding, formatSceneLocator, formatGroupLocator)),
            );
        const hiddenCount = findings.length - maxFindingsShownPerSeverity;
        if (hiddenCount > 0) {
            lines.push(`  ... and ${hiddenCount} more ${severityLabel}(s) not shown.`);
        }
    };

    pushSection('Errors (fix these before finishing):', errors, 'error');
    pushSection(formatWarningsHeading(errors.length > 0), warnings, 'warning');

    // Hidden errors are fixed by re-linting after the visible ones; hidden warnings are not an
    // invitation to re-lint, or the footer would undo the advisory heading right above it.
    if (errors.length > maxFindingsShownPerSeverity) {
        lines.push(
            '',
            `Only the first ${maxFindingsShownPerSeverity} of each are listed. Fix these first, then call ${TOOL_NAMES.lintVideo} again to see the rest, or pass check_name to see every finding from one check.`,
        );
    } else if (warnings.length > maxFindingsShownPerSeverity) {
        lines.push(
            '',
            `Only the first ${maxFindingsShownPerSeverity} warnings are listed. Pass check_name to see every finding from one check if you need the rest.`,
        );
    }

    return lines.join('\n');
}

/**
 * Findings the reference video has on the scene each copy came from. They're listed so the agent
 * knows the checks ran and why these were not raised, but they need no action.
 */
function formatReferenceMatches(
    findings: LintFinding[],
    formatSceneLocator: SceneLocatorFormatter,
    formatGroupLocator: GroupLocatorFormatter,
): string {
    if (findings.length === 0) {
        return '';
    }
    const listed = findings
        .map(
            (finding) =>
                `  - ${formatLocation(finding, formatSceneLocator, formatGroupLocator)}${finding.check}`,
        )
        .join('\n');
    return `\n\nMatches the reference video (deliberate — leave these as they are, no action needed):\n${listed}`;
}

/**
 * Runs the combined video linter against the bound Luminary
 * and returns one report with errors and warnings clearly separated. The set of
 * checks is whatever `ALL_LINT_CHECKS` holds. The checks never modify the video; the
 * one write is auto-timing, which first lands every
 * not-yet-timed text and icon on its narration word.
 */
export class LintVideoTool extends Tool<typeof lintVideoSchema> {
    // Deferred but serial: the contrast check probes by recolouring live text assets and
    // re-rendering the shared canvas, so it must not overlap the vision reviews' captures.
    public override runsAfterEdits = true;

    protected override readonly schema = lintVideoSchema;
    protected override readonly toolName = TOOL_NAMES.lintVideo;
    public override readonly subagentAccess = SubagentAccess.CAPTURE;
    protected override readonly toolDescription =
        'Run a battery of quality checks on the current video and return one list of findings split into errors and warnings. Errors are hard visual defects you must fix. Warnings are heuristics — "probably wrong but maybe intentional", plus opportunities — that you weigh on your own and may leave as they are; a warning-free result is not required.';

    private readonly sceneAddressing: SceneAddressing;

    public constructor(args: LintVideoConstructorArgs) {
        const { sceneAddressing, ...baseArgs } = args;
        super(baseArgs as ToolConstructorArgs);
        this.sceneAddressing = sceneAddressing;
    }

    public override getToolDisplayName(): string {
        return 'Checking video for issues';
    }

    public get statusIcon(): string {
        return 'rule';
    }

    public async execute(args: LintVideoArgs, context: ToolExecutionContext) {
        // Linting means the build is essentially done, so nothing should still read as
        // pending through the final quality pass. Not so for a subagent, whose siblings
        // may still be filling their scenes.
        if (context.subagentDescription === undefined) {
            this.dispatch(clearAllSceneBuilding());
        }

        if (isReviewSkipped(this.getState())) {
            return reviewSkippedResult();
        }

        // The review phase often opens with a lint rather than an image_ask, so the stage
        // has to be announced from here too or the phase would go unlabelled until the
        // first vision call.
        announceCreationStage(
            this.dispatch,
            this.getState,
            context,
            CreationStage.REVIEWING,
            getVideoSizeAnalyticsProperties(this.luminary),
        );

        // Auto-fixes change the video, so they run before the checks, which then see the fixed
        // result. Only the root agent runs them: a subagent may be a read-only reviewer, and what its
        // siblings built is fixed when the root agent lints.
        const formatSceneLocator = this.buildSceneLocatorFormatter();
        const autoFixes = context.subagentDescription
            ? []
            : AUTO_FIXES.filter(({ name }) => !args.check_name || args.check_name === name);
        const autoFixResults = new Map<AutoFixName, AutoFixResult>();
        for (const autoFix of autoFixes) {
            const result = await autoFix
                .run({
                    luminary: this.luminary,
                    dispatch: this.dispatch,
                    getState: this.getState,
                    parentActionGroup: context.parentActionGroup,
                    formatSceneLocator,
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    return { fixedCount: 0, note: '' };
                });
            autoFixResults.set(autoFix.name, result);
        }
        const autoFixNotes = [...autoFixResults.values()].map(({ note }) => note).join('');
        const autoFixCounts = Object.fromEntries(
            ALL_AUTO_FIXES.map((name) => [
                `auto_fixed_${name}`,
                autoFixResults.get(name)?.fixedCount ?? 0,
            ]),
        );
        if (isAutoFixName(args.check_name)) {
            analytics.track('Agent: Video Linted', {
                check_name: args.check_name,
                ...autoFixCounts,
            });
            return autoFixNotes || 'Nothing needed fixing.';
        }

        // Deliberately no per-scene reviewing indicator here, unlike image_ask: lint is
        // fast enough that showing one only flashes, which reads as a glitch rather than
        // as feedback.
        const fullReport = await runVideoLint(
            this.luminary,
            this.getState,
            args.check_name ? { only: [args.check_name] } : undefined,
        );
        // A subagent working alongside others can only fix its own scene groups, so it sees only
        // their findings; the rest belong to its siblings and to the agent that started it.
        const scope = context.subagentScope;
        const scopedFindings = scope
            ? fullReport.findings.filter((f) => scope.ownsFinding(f))
            : fullReport.findings;
        const hiddenOutsideScope = fullReport.findings.length - scopedFindings.length;
        // A scene copied from a reference that fails the same check there is showing the look the
        // user asked for, so it is reported as matching the reference rather than as a defect.
        const matchingReference = scopedFindings.filter(matchesReferenceFinding);
        const report = {
            ...fullReport,
            findings: scopedFindings.filter((finding) => !matchesReferenceFinding(finding)),
        };
        analytics.track('Agent: Video Linted', {
            ...buildCheckFindingCounts(
                report.checksRun,
                report.findings,
                (check) => LINT_CHECK_SEVERITIES[check],
            ),
            check_name: args.check_name ?? null,
            ...autoFixCounts,
            reference_matched_count: matchingReference.length,
        });
        const formatGroupLocator = this.buildGroupLocatorFormatter();
        const formatted =
            autoFixNotes +
            formatLintReport(
                report,
                formatSceneLocator,
                formatGroupLocator,
                args.check_name ? Infinity : MAX_FINDINGS_SHOWN_PER_SEVERITY,
            ) +
            formatReferenceMatches(matchingReference, formatSceneLocator, formatGroupLocator);
        return scope && hiddenOutsideScope > 0
            ? `${formatted}\n\nThis covers only your scene groups: ${hiddenOutsideScope} finding(s) outside your scene groups are not shown, since other subagents and the agent that started you handle those.`
            : formatted;
    }

    /**
     * Build the scene-locator renderer for the hosting agent's addressing mode. In scene_id mode
     * each id is shortened to the same handle shown in the video state; in index mode the id is
     * resolved back to its current position — the scene_index the legacy edit tools take.
     */
    private buildSceneLocatorFormatter(): SceneLocatorFormatter {
        const scenes = this.luminary.scenes;
        if (this.sceneAddressing === 'index') {
            return (sceneId) => `scene ${scenes.findIndex((scene) => scene.id === sceneId)}`;
        }
        const shortSceneIds = computeSceneShortIds(scenes.map((scene) => scene.id));
        return (sceneId) => `scene_id ${shortSceneIds.get(sceneId) ?? sceneId}`;
    }

    /**
     * Build the scene-group-locator renderer for the hosting agent's addressing mode. In scene_id
     * mode a group is shown by the same short scene_group_id the group tools take; in index mode by
     * its positional index (which the legacy agents still address groups by).
     */
    private buildGroupLocatorFormatter(): GroupLocatorFormatter {
        const sceneGroups = this.luminary.sceneGroups;
        if (this.sceneAddressing === 'index') {
            return (sceneGroupIndex) => `scene group ${sceneGroupIndex}`;
        }
        const shortGroupIds = computeSceneGroupShortIds(sceneGroups.map((group) => group.id));
        return (sceneGroupIndex) => {
            const id = sceneGroups[sceneGroupIndex]?.id;
            return id
                ? `scene group ${shortGroupIds.get(id) ?? id}`
                : `scene group ${sceneGroupIndex}`;
        };
    }
}
